<template>
  <div class="p-6 mx-auto">
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">
        Statistics Documentation
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Learn how to view and analyze statistics about your events, songs, users, and voting
        activity.
      </p>
    </div>

    <div class="space-y-6">
      <!-- Overview Section -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Overview</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          The Statistics page provides comprehensive analytics about your jukebox system, including
          event performance, song popularity, user activity, and voting patterns. This page is only
          accessible to users with admin privileges.
        </p>
      </section>

      <!-- Overall Statistics -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          Overall Statistics
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          The statistics page displays key metrics including:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li><strong>Total Events:</strong> Number of events created in the system</li>
          <li><strong>Total Songs:</strong> Number of songs in your library</li>
          <li><strong>Total Users:</strong> Number of registered user accounts</li>
          <li><strong>Total Votes:</strong> Total number of votes cast across all events</li>
        </ul>
      </section>

      <!-- Event Statistics -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Event Statistics</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          View detailed statistics for individual events:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Number of votes received per event</li>
          <li>Most popular songs in each event</li>
          <li>
            Event duration (when one song is being played, the duration is displayed as
            "Playing...") and participation rates
          </li>
          <li>Comparison between events</li>
        </ul>
      </section>

      <!-- Song Statistics -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Song Statistics</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          Analyze song performance across all events:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Most voted songs (all-time favorites)</li>
          <li>Songs by genre popularity</li>
          <li>Songs by category performance</li>
          <li>Vote distribution across songs</li>
        </ul>
      </section>

      <!-- User Activity -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">User Activity</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          Track user engagement and participation:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Most active users (by vote count)</li>
          <li>User participation rates</li>
        </ul>
      </section>

      <!-- Voting Patterns -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Voting Patterns</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">Understand voting behavior and trends:</p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Peak voting times</li>
          <li>Vote distribution throughout events</li>
          <li>Trends in song preferences</li>
          <li>Comparison of voting activity across events</li>
        </ul>
      </section>

      <!-- Using Statistics -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Using Statistics</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">Statistics can help you:</p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Identify popular songs to include in future events</li>
          <li>Understand user preferences and engagement</li>
          <li>Plan event timing based on peak activity</li>
          <li>Make data-driven decisions about your song library</li>
          <li>Track system growth and usage over time</li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: "StatisticsDocumentation"
};
</script>
